import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client with improved error handling and retry logic
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage
  },
  global: {
    headers: {
      'X-Client-Info': 'aix-web'
    }
  },
  // Add retry configuration
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Add connection health check
export const checkConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('count')
      .limit(1)
      .single();
      
    return !error;
  } catch {
    return false;
  }
};

// Add safe query wrapper with retry mechanism
export const safeQuery = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  retries = 3
): Promise<T | null> => {
  let attempt = 0;
  const baseDelay = 1000; // 1 second
  
  while (attempt < retries) {
    try {
      const { data, error } = await operation();
      
      // Handle specific error types
      if (error) {
        if (error.code === 'PGRST116') {
          console.warn('Resource not found:', error);
          return null;
        }
        if (error.code === '23505') {
          console.warn('Duplicate record:', error);
          return null;
        }
        // Network errors should retry
        if (error.code === 'NETWORK_ERROR' || error.message?.includes('Failed to fetch')) {
          attempt++;
          await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
          continue;
        }
        throw error;
      }
      
      return data;
    } catch (error: any) {
      if (attempt === retries - 1) {
        console.error('Database operation failed after retries:', error);
        return null;
      }
      attempt++;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
    }
  }
  
  return null;
};

// Add connection recovery function
export const recoverConnection = async () => {
  try {
    // First check if we can reach Supabase
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      // Session error - try to refresh
      const { data: { session: newSession }, error: refreshError } = 
        await supabase.auth.refreshSession();
        
      if (refreshError) throw refreshError;
      if (!newSession) throw new Error('No session after refresh');
    }

    // Test database connection
    const { data, error: testError } = await supabase
      .from('profiles')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (testError && testError.code !== 'PGRST116') {
      throw testError;
    }

    return true;
  } catch (error) {
    console.error('Failed to recover connection:', error);
    return false;
  }
};

// Export singleton instance
export default supabase;
